import { isPrimaryUnmodifiedClick } from "@pathwright/utils/src/event/click"
import { normalizeHash } from "@pathwright/utils/src/url/hash"
import { MouseEvent, useCallback, useEffect } from "react"
import { useHistory, useLocation } from "react-router-dom"

export const AUGMENTATION_PANEL_HASH_NAVIGATION_EVENT =
  "augmentation-panel-hash-navigation"

export function dispatchAugmentationPanelHashNavigation(hashKey?: string) {
  const hash = normalizeHash(hashKey)
  if (!hash || typeof window === "undefined") return

  window.dispatchEvent(
    new CustomEvent(AUGMENTATION_PANEL_HASH_NAVIGATION_EVENT, {
      detail: hash
    })
  )
}

export function useAugmentationPanelHashClickHandler(hashKey?: string) {
  return useCallback(
    (event: MouseEvent) => {
      if (!hashKey || !isPrimaryUnmodifiedClick(event)) return

      dispatchAugmentationPanelHashNavigation(hashKey)
    },
    [hashKey]
  )
}

export function useSetHashKey() {
  const history = useHistory()
  const location = useLocation<any>()

  return useCallback(
    (hashKey?: string) => {
      if (!hashKey) return

      history.replace({
        ...location,
        hash: normalizeHash(hashKey)
      })
      dispatchAugmentationPanelHashNavigation(hashKey)
    },
    [history, location]
  )
}

/** Subscribe to augmentation panel hash changes (URL hash + imperative navigation event). */
export function useAugmentationPanelHashNavigation(
  onNavigate: (hash: string) => void
) {
  const location = useLocation()

  useEffect(() => {
    if (location.hash) {
      onNavigate(location.hash)
    }
  }, [location.hash, onNavigate])

  useEffect(() => {
    const handleHashNavigation = (event: Event) => {
      onNavigate(
        (event as CustomEvent<string>).detail || window.location.hash
      )
    }

    window.addEventListener(
      AUGMENTATION_PANEL_HASH_NAVIGATION_EVENT,
      handleHashNavigation
    )

    return () => {
      window.removeEventListener(
        AUGMENTATION_PANEL_HASH_NAVIGATION_EVENT,
        handleHashNavigation
      )
    }
  }, [onNavigate])
}
